"use client";

import { useState, useCallback, useRef } from "react";
import Link from "next/link";
import {
  ArrowLeft,
  ArrowRight,
  Building2,
  Info,
  AlertTriangle,
  TrendingUp,
  Users,
  Calculator,
  ChevronDown,
  ChevronUp,
  Table,
  BadgePercent,
} from "lucide-react";
import { Breadcrumbs } from "@/components/seo/Breadcrumbs";
import { RelatedLinks } from "@/components/seo/RelatedLinks";
import {
  StructuredData,
  generateHowToSchema,
} from "@/components/seo/StructuredData";
import {
  type IsverenMaliyetiSonuc,
  BRUT_ASGARI_UCRET,
  hesaplaIsverenMaliyeti,
  formatTL,
  formatOran,
  YILI,
} from "./isveren-maliyeti-engine";

const howToSchema = generateHowToSchema({
  title: "İşveren Maliyeti Nasıl Hesaplanır?",
  description:
    "Brüt maaş ve çalışan sayısını girerek 2026 güncel SGK oranlarına göre toplam istihdam maliyetinizi hesaplayın.",
  totalTime: "PT2M",
  steps: [
    {
      name: "Brüt Maaşı Girin",
      text: "Çalışana ödenen aylık brüt maaşı TL olarak girin.",
    },
    {
      name: "Çalışan Sayısını Belirleyin",
      text: "Aynı brüt maaşla çalışan işçi sayısını girerek toplam maliyeti hesaplayın.",
    },
    {
      name: "İndirimleri Seçin",
      text: "5 puanlık SGK indirimi uygulanıyorsa işaretleyin.",
    },
    {
      name: "Sonuçları İnceleyin",
      text: "Çalışan başına ve toplam işveren maliyetini detaylı olarak görüntüleyin.",
    },
  ],
});

// ─── SSS ──────────────────────────────────────────────────────
function SSSSoru({ soru, cevap }: { soru: string; cevap: string }) {
  const [acik, setAcik] = useState(false);
  return (
    <div className="border border-gray-200 rounded-xl overflow-hidden">
      <button
        type="button"
        onClick={() => setAcik((p) => !p)}
        className="w-full flex items-center justify-between p-5 text-left hover:bg-gray-50 transition-colors"
        aria-expanded={acik}
      >
        <span className="font-semibold text-gray-900 pr-4">{soru}</span>
        {acik ? (
          <ChevronUp className="w-5 h-5 text-gray-400 shrink-0" />
        ) : (
          <ChevronDown className="w-5 h-5 text-gray-400 shrink-0" />
        )}
      </button>
      {acik && (
        <div className="px-5 pb-5">
          <p className="text-gray-600 leading-relaxed">{cevap}</p>
        </div>
      )}
    </div>
  );
}

// ─── Sonuç satırı ─────────────────────────────────────────────
function SonucSatir({
  label,
  value,
  variant = "normal",
  bold = false,
}: {
  label: string;
  value: string;
  variant?: "normal" | "kesinti" | "eklenti" | "toplam" | "indirim";
  bold?: boolean;
}) {
  const renkler = {
    normal: "text-gray-900",
    kesinti: "text-red-600",
    eklenti: "text-blue-600",
    toplam: "text-emerald-600",
    indirim: "text-green-600",
  };
  return (
    <div
      className={`flex justify-between items-center py-1.5 ${bold ? "pt-3 border-t border-gray-200" : ""}`}
    >
      <span
        className={`text-sm ${bold ? "font-bold text-gray-900" : "text-gray-600"}`}
      >
        {label}
      </span>
      <span
        className={`text-sm font-medium tabular-nums ${bold ? "text-base font-bold" : ""} ${renkler[variant]}`}
      >
        {value}
      </span>
    </div>
  );
}

// ─── Ana Bileşen ──────────────────────────────────────────────
export default function IsverenMaliyetiHesaplayiciClient() {
  const [brutMaas, setBrutMaas] = useState("");
  const [calisanSayisi, setCalisanSayisi] = useState("1");
  const [besPuanIndirim, setBesPuanIndirim] = useState(true);
  const [sonuc, setSonuc] = useState<IsverenMaliyetiSonuc | null>(null);
  const [hata, setHata] = useState<string | null>(null);
  const [hesaplandi, setHesaplandi] = useState(false);
  const [tabloAcik, setTabloAcik] = useState(false);

  const maasInputRef = useRef<HTMLInputElement>(null);

  const hesapla = useCallback(() => {
    setHata(null);
    const maas = parseFloat(brutMaas);
    const sayi = parseInt(calisanSayisi) || 1;

    if (!brutMaas || isNaN(maas) || maas <= 0) {
      setHata("Lütfen geçerli bir brüt maaş giriniz.");
      setSonuc(null);
      setHesaplandi(false);
      return;
    }

    const hesapSonuc = hesaplaIsverenMaliyeti({
      brutMaas: maas,
      calisanSayisi: sayi,
      besPuanIndirimVar: besPuanIndirim,
    });

    setSonuc(hesapSonuc);
    setHesaplandi(true);
    setHata(null);
  }, [brutMaas, calisanSayisi, besPuanIndirim]);

  const asgariUcretDoldur = useCallback(() => {
    setBrutMaas(BRUT_ASGARI_UCRET.toString());
  }, []);

  const sifirla = useCallback(() => {
    setBrutMaas("");
    setCalisanSayisi("1");
    setBesPuanIndirim(true);
    setSonuc(null);
    setHata(null);
    setHesaplandi(false);
    setTabloAcik(false);
  }, []);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === "Enter") {
        e.preventDefault();
        hesapla();
      }
    },
    [hesapla]
  );

  // İlk ayın değerleri (özet için)
  const ilkAy = sonuc?.aylikDetaylar[0];

  return (
    <>
      <StructuredData data={[howToSchema]} />

      <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
        {/* Hero */}
        <section className="bg-gradient-to-br from-teal-600 to-cyan-700 text-white py-16">
          <div className="max-w-4xl mx-auto px-4">
            <Breadcrumbs
              items={[
                { label: "Araçlar", href: "/araclar" },
                {
                  label: "İşveren Maliyeti Hesaplayıcı",
                  href: "/araclar/isveren-maliyeti-hesaplayici",
                },
              ]}
              className="mb-8 text-white/70"
            />

            <Link
              href="/araclar"
              className="inline-flex items-center gap-2 text-white/80 hover:text-white mb-6 transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              Tüm Araçlar
            </Link>

            <div className="flex items-center gap-4 mb-6">
              <div className="p-4 bg-white/20 rounded-xl backdrop-blur-sm">
                <Building2 className="w-8 h-8" />
              </div>
              <div>
                <h1 className="text-3xl sm:text-4xl font-bold">
                  İşveren Maliyeti Hesaplayıcı
                </h1>
                <span className="inline-block mt-2 px-3 py-1 bg-white/20 rounded-full text-sm font-medium backdrop-blur-sm">
                  {YILI} Güncel SGK Oranları
                </span>
              </div>
            </div>
            <p className="text-lg text-white/90 max-w-2xl">
              Bir çalışanın işverene toplam maliyetini hesaplayın. SGK işveren
              payı, işsizlik sigortası, iş kazası primi ve 5 puanlık indirim
              dahil.
            </p>
          </div>
        </section>

        {/* Hesaplama Formu */}
        <section className="max-w-4xl mx-auto px-4 py-12">
          <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
            <div className="p-6 sm:p-8">
              {/* SGK Oranları Özeti */}
              <div className="mb-8 p-4 bg-teal-50 rounded-xl border border-teal-100">
                <h2 className="text-sm font-semibold text-gray-900 mb-3">
                  {YILI} SGK İşveren Prim Oranları
                </h2>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
                  <div>
                    <span className="text-gray-500 block">MYÖ</span>
                    <span className="font-bold text-gray-900">%11</span>
                  </div>
                  <div>
                    <span className="text-gray-500 block">GSS</span>
                    <span className="font-bold text-gray-900">%7,5</span>
                  </div>
                  <div>
                    <span className="text-gray-500 block">
                      İş Kazası (KVSK)
                    </span>
                    <span className="font-bold text-gray-900">%2</span>
                  </div>
                  <div>
                    <span className="text-gray-500 block">İşsizlik</span>
                    <span className="font-bold text-gray-900">%2</span>
                  </div>
                </div>
              </div>

              {/* Girdiler */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mb-6">
                {/* Brüt Maaş */}
                <div>
                  <label
                    htmlFor="brutMaas"
                    className="block text-sm font-semibold text-gray-900 mb-2"
                  >
                    Aylık Brüt Maaş
                  </label>
                  <div className="relative">
                    <input
                      ref={maasInputRef}
                      id="brutMaas"
                      type="number"
                      inputMode="decimal"
                      min="0"
                      step="100"
                      value={brutMaas}
                      onChange={(e) => setBrutMaas(e.target.value)}
                      onKeyDown={handleKeyDown}
                      placeholder="0,00"
                      className="w-full px-4 py-3 pr-12 border border-gray-300 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-teal-500 text-gray-900 placeholder-gray-400 transition-shadow"
                    />
                    <span className="absolute right-4 top-1/2 -translate-y-1/2 text-sm text-gray-400 pointer-events-none">
                      ₺
                    </span>
                  </div>
                  <button
                    type="button"
                    onClick={asgariUcretDoldur}
                    className="mt-2 text-xs text-teal-600 hover:text-teal-700 font-medium transition-colors"
                  >
                    {YILI} Asgari Ücret Uygula ({formatTL(BRUT_ASGARI_UCRET)})
                  </button>
                </div>

                {/* Çalışan Sayısı */}
                <div>
                  <label
                    htmlFor="calisanSayisi"
                    className="block text-sm font-semibold text-gray-900 mb-2"
                  >
                    Çalışan Sayısı
                  </label>
                  <div className="relative">
                    <input
                      id="calisanSayisi"
                      type="number"
                      inputMode="numeric"
                      min="1"
                      max="10000"
                      value={calisanSayisi}
                      onChange={(e) => setCalisanSayisi(e.target.value)}
                      onKeyDown={handleKeyDown}
                      placeholder="1"
                      className="w-full px-4 py-3 pr-16 border border-gray-300 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-teal-500 text-gray-900 placeholder-gray-400 transition-shadow"
                    />
                    <span className="absolute right-4 top-1/2 -translate-y-1/2 text-sm text-gray-400 pointer-events-none">
                      Kişi
                    </span>
                  </div>
                  <p className="mt-2 text-xs text-gray-500">
                    Aynı brüt maaşla çalışan sayısı
                  </p>
                </div>
              </div>

              {/* 5 Puan İndirimi */}
              <div className="mb-8">
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={besPuanIndirim}
                    onChange={(e) => setBesPuanIndirim(e.target.checked)}
                    className="w-5 h-5 text-teal-600 border-gray-300 rounded focus:ring-teal-500"
                  />
                  <div>
                    <span className="text-sm font-semibold text-gray-900">
                      5 Puanlık SGK İndirimi Uygula
                    </span>
                    <span className="block text-xs text-gray-500 mt-0.5">
                      5510 sayılı Kanun Md. 81/ı — Hazine destekli %5 prim
                      indirimi
                    </span>
                  </div>
                </label>
              </div>

              {/* Hata */}
              {hata && (
                <div className="mb-6 flex items-start gap-3 p-4 bg-red-50 border border-red-200 rounded-xl">
                  <AlertTriangle className="w-5 h-5 text-red-500 shrink-0 mt-0.5" />
                  <p className="text-sm text-red-700">{hata}</p>
                </div>
              )}

              {/* Butonlar */}
              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={hesapla}
                  className="flex-1 bg-teal-600 text-white py-4 rounded-xl font-semibold hover:bg-teal-700 active:bg-teal-800 transition-colors shadow-sm"
                >
                  <span className="flex items-center justify-center gap-2">
                    <Calculator className="w-5 h-5" />
                    Hesapla
                  </span>
                </button>
                {hesaplandi && (
                  <button
                    type="button"
                    onClick={sifirla}
                    className="px-6 py-4 border border-gray-300 rounded-xl font-medium text-gray-700 hover:bg-gray-50 transition-colors"
                  >
                    Sıfırla
                  </button>
                )}
              </div>
            </div>

            {/* Sonuç */}
            {sonuc && ilkAy && (
              <div className="border-t border-gray-200 bg-gray-50 p-6 sm:p-8">
                {/* Özet Kartları */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-8">
                  <div className="bg-teal-600 text-white rounded-xl p-4">
                    <span className="text-xs text-teal-100 font-medium block">
                      Aylık Toplam Maliyet
                    </span>
                    <span className="text-lg sm:text-xl font-bold mt-1 block tabular-nums">
                      {formatTL(ilkAy.toplamIsverenMaliyet)}
                    </span>
                    <span className="text-xs text-teal-200 mt-1 block">
                      Çalışan başı
                    </span>
                  </div>
                  <div className="bg-white rounded-xl p-4 border border-gray-200">
                    <span className="text-xs text-gray-500 font-medium block">
                      Aylık Ek Maliyet
                    </span>
                    <span className="text-lg sm:text-xl font-bold text-blue-600 mt-1 block tabular-nums">
                      {formatTL(ilkAy.toplamIsverenEkMaliyet)}
                    </span>
                    <span className="text-xs text-gray-400 mt-1 block">
                      Brüt üzeri
                    </span>
                  </div>
                  <div className="bg-white rounded-xl p-4 border border-gray-200">
                    <span className="text-xs text-gray-500 font-medium block">
                      Maliyet Oranı
                    </span>
                    <span className="text-lg sm:text-xl font-bold text-violet-600 mt-1 block tabular-nums">
                      {formatOran(sonuc.isverenMaliyetOrani)}
                    </span>
                    <span className="text-xs text-gray-400 mt-1 block">
                      Brüt üzerinden ek
                    </span>
                  </div>
                  <div className="bg-white rounded-xl p-4 border border-gray-200">
                    <span className="text-xs text-gray-500 font-medium block">
                      Çalışan Net Maaş
                    </span>
                    <span className="text-lg sm:text-xl font-bold text-emerald-600 mt-1 block tabular-nums">
                      {formatTL(ilkAy.netMaas)}
                    </span>
                    <span className="text-xs text-gray-400 mt-1 block">
                      Ocak ayı
                    </span>
                  </div>
                </div>

                {/* Çoklu çalışan */}
                {sonuc.calisanSayisi > 1 && (
                  <div className="mb-8 p-4 bg-blue-50 rounded-xl border border-blue-100">
                    <div className="flex items-center gap-2 mb-2">
                      <Users className="w-5 h-5 text-blue-600" />
                      <h3 className="font-bold text-gray-900">
                        {sonuc.calisanSayisi} Çalışan Toplam Maliyet
                      </h3>
                    </div>
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <span className="text-gray-500 block">
                          Aylık Toplam
                        </span>
                        <span className="font-bold text-xl text-blue-700 tabular-nums">
                          {formatTL(sonuc.toplamAylikIsverenMaliyet)}
                        </span>
                      </div>
                      <div>
                        <span className="text-gray-500 block">
                          Yıllık Toplam
                        </span>
                        <span className="font-bold text-xl text-blue-700 tabular-nums">
                          {formatTL(sonuc.toplamYillikIsverenMaliyet)}
                        </span>
                      </div>
                    </div>
                  </div>
                )}

                {/* Detaylı Döküm */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                  {/* İşveren Kesintileri */}
                  <div className="bg-white rounded-xl border border-gray-200 p-5">
                    <div className="flex items-center gap-2 mb-4">
                      <Building2 className="w-5 h-5 text-teal-600" />
                      <h3 className="font-bold text-gray-900">
                        İşveren Ek Maliyeti (Aylık)
                      </h3>
                    </div>
                    <div className="space-y-0.5">
                      <SonucSatir
                        label="Brüt maaş"
                        value={formatTL(sonuc.brutMaas)}
                      />
                      <SonucSatir
                        label="SGK MYÖ (%11)"
                        value={`+${formatTL(ilkAy.sgkIsverenMyo)}`}
                        variant="eklenti"
                      />
                      <SonucSatir
                        label="SGK GSS (%7,5)"
                        value={`+${formatTL(ilkAy.sgkIsverenGss)}`}
                        variant="eklenti"
                      />
                      <SonucSatir
                        label="İş Kazası / KVSK (%2)"
                        value={`+${formatTL(ilkAy.sgkIsverenKvsk)}`}
                        variant="eklenti"
                      />
                      <SonucSatir
                        label="İşsizlik İşveren (%2)"
                        value={`+${formatTL(ilkAy.issizlikIsveren)}`}
                        variant="eklenti"
                      />
                      {besPuanIndirim && (
                        <SonucSatir
                          label="5 Puan İndirimi (%5)"
                          value={`-${formatTL(ilkAy.besPuanIndirim)}`}
                          variant="indirim"
                        />
                      )}
                      <SonucSatir
                        label="Toplam İşveren Maliyeti"
                        value={formatTL(ilkAy.toplamIsverenMaliyet)}
                        variant="toplam"
                        bold
                      />
                    </div>
                  </div>

                  {/* İşçi Kesintileri */}
                  <div className="bg-white rounded-xl border border-gray-200 p-5">
                    <div className="flex items-center gap-2 mb-4">
                      <TrendingUp className="w-5 h-5 text-emerald-600" />
                      <h3 className="font-bold text-gray-900">
                        İşçi Kesintileri (Ocak)
                      </h3>
                    </div>
                    <div className="space-y-0.5">
                      <SonucSatir
                        label="Brüt maaş"
                        value={formatTL(sonuc.brutMaas)}
                      />
                      <SonucSatir
                        label="SGK işçi payı (%14)"
                        value={`-${formatTL(ilkAy.sgkIsci)}`}
                        variant="kesinti"
                      />
                      <SonucSatir
                        label="İşsizlik işçi payı (%1)"
                        value={`-${formatTL(ilkAy.issizlikIsci)}`}
                        variant="kesinti"
                      />
                      <SonucSatir
                        label="Gelir vergisi"
                        value={`-${formatTL(ilkAy.gelirVergisi)}`}
                        variant="kesinti"
                      />
                      <SonucSatir
                        label="Damga vergisi (%0,759)"
                        value={`-${formatTL(ilkAy.damgaVergisi)}`}
                        variant="kesinti"
                      />
                      <SonucSatir
                        label="Net Maaş"
                        value={formatTL(ilkAy.netMaas)}
                        variant="toplam"
                        bold
                      />
                    </div>
                  </div>
                </div>

                {/* Yıllık Özet */}
                <div className="bg-white rounded-xl border border-gray-200 p-5 mb-6">
                  <div className="flex items-center gap-2 mb-4">
                    <BadgePercent className="w-5 h-5 text-teal-600" />
                    <h3 className="font-bold text-gray-900">
                      Yıllık Toplam (1 Çalışan)
                    </h3>
                  </div>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 text-sm">
                    <div>
                      <span className="text-gray-500 block mb-1">
                        Yıllık Brüt
                      </span>
                      <span className="font-bold text-gray-900 tabular-nums">
                        {formatTL(sonuc.yillikBrutUcret)}
                      </span>
                    </div>
                    <div>
                      <span className="text-gray-500 block mb-1">
                        Yıllık Net
                      </span>
                      <span className="font-bold text-emerald-600 tabular-nums">
                        {formatTL(sonuc.yillikNetUcret)}
                      </span>
                    </div>
                    <div>
                      <span className="text-gray-500 block mb-1">
                        Yıllık İşveren Maliyeti
                      </span>
                      <span className="font-bold text-teal-600 tabular-nums">
                        {formatTL(sonuc.yillikToplamIsverenMaliyet)}
                      </span>
                    </div>
                  </div>
                </div>

                {/* 12 Aylık Tablo */}
                <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                  <button
                    type="button"
                    onClick={() => setTabloAcik((p) => !p)}
                    className="w-full flex items-center justify-between p-5 hover:bg-gray-50 transition-colors"
                  >
                    <div className="flex items-center gap-2">
                      <Table className="w-5 h-5 text-teal-600" />
                      <span className="font-bold text-gray-900">
                        12 Aylık Detaylı Tablo
                      </span>
                    </div>
                    {tabloAcik ? (
                      <ChevronUp className="w-5 h-5 text-gray-400" />
                    ) : (
                      <ChevronDown className="w-5 h-5 text-gray-400" />
                    )}
                  </button>

                  {tabloAcik && (
                    <div className="overflow-x-auto border-t border-gray-200">
                      <table className="w-full text-xs sm:text-sm whitespace-nowrap">
                        <thead>
                          <tr className="bg-teal-50 border-b border-gray-200">
                            <th className="text-left py-3 px-3 font-semibold text-gray-900">
                              Ay
                            </th>
                            <th className="text-right py-3 px-3 font-semibold text-gray-900">
                              Brüt
                            </th>
                            <th className="text-right py-3 px-3 font-semibold text-gray-900">
                              İşveren Ek
                            </th>
                            <th className="text-right py-3 px-3 font-semibold text-gray-900">
                              Toplam Maliyet
                            </th>
                            <th className="text-right py-3 px-3 font-semibold text-gray-900">
                              Net Maaş
                            </th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                          {sonuc.aylikDetaylar.map((ay) => (
                            <tr key={ay.ay} className="hover:bg-gray-50">
                              <td className="py-2.5 px-3 font-medium text-gray-900">
                                {ay.ayAdi}
                              </td>
                              <td className="py-2.5 px-3 text-right tabular-nums text-gray-700">
                                {formatTL(ay.brutMaas)}
                              </td>
                              <td className="py-2.5 px-3 text-right tabular-nums text-blue-600">
                                +{formatTL(ay.toplamIsverenEkMaliyet)}
                              </td>
                              <td className="py-2.5 px-3 text-right tabular-nums font-semibold text-teal-600">
                                {formatTL(ay.toplamIsverenMaliyet)}
                              </td>
                              <td className="py-2.5 px-3 text-right tabular-nums text-emerald-600">
                                {formatTL(ay.netMaas)}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Bilgilendirme */}
          <div className="mt-8 p-5 bg-amber-50 rounded-xl border border-amber-100">
            <div className="flex items-start gap-3">
              <Info className="w-5 h-5 text-amber-600 mt-0.5 shrink-0" />
              <div>
                <h3 className="font-semibold text-gray-900 mb-2">
                  Önemli Bilgiler
                </h3>
                <ul className="text-sm text-gray-600 space-y-1.5">
                  <li>
                    • İş kazası / meslek hastalığı oranı sektöre göre %1–%6,5
                    arasında değişir. Hesaplamada varsayılan %2 kullanılmıştır.
                  </li>
                  <li>
                    • 5 puanlık indirim, SGK prim borcu olmayan ve kayıt dışı
                    işçi çalıştırmayan işverenlere uygulanır.
                  </li>
                  <li>
                    • Bu hesaplama {YILI} yılı güncel oranlarına göredir. Kesin
                    maliyet için muhasebe danışmanınıza başvurunuz.
                  </li>
                  <li>
                    • Kıdem tazminatı, yemek, yol gibi yan haklar bu hesaplamaya
                    dahil değildir.
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* Rehber & SSS */}
        <section className="max-w-4xl mx-auto px-4 py-8">
          <div className="prose prose-gray max-w-none">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">
              İşveren Maliyeti Hesaplama Rehberi ({YILI})
            </h2>
            <div className="bg-white rounded-2xl p-6 sm:p-8 border border-gray-100 shadow-sm space-y-8">
              <div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  İşveren Maliyeti Nedir?
                </h3>
                <p className="text-gray-700 leading-relaxed">
                  İşveren maliyeti, bir çalışana ödenen brüt maaşın üzerine
                  işverenin yasal olarak ödemekle yükümlü olduğu SGK primleri ve
                  işsizlik sigortası primlerinin toplamıdır. Bu maliyet
                  çalışanın eline geçen net maaştan çok daha yüksektir.
                </p>
              </div>

              <div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  Hesaplama Formülü
                </h3>
                <div className="bg-teal-50 rounded-xl p-5 space-y-2 text-sm">
                  <p className="text-gray-700">
                    <strong className="text-teal-700">Toplam Maliyet</strong> =
                    Brüt Maaş + SGK İşveren (MYÖ + GSS + KVSK) + İşsizlik
                    İşveren – 5 Puan İndirimi
                  </p>
                  <p className="text-gray-700">
                    <strong className="text-teal-700">Varsayılan</strong> = Brüt
                    × (1 + %20.5 + %2 – %5) = Brüt × 1,175
                  </p>
                </div>
              </div>

              <div>
                <h3 className="text-xl font-semibold text-gray-900 mb-4">
                  Sık Sorulan Sorular
                </h3>
                <div className="space-y-3">
                  <SSSSoru
                    soru="İşveren maliyeti brüt maaşın yüzde kaçıdır?"
                    cevap="5 puanlık indirim ile toplam işveren prim yükü brüt maaşın yaklaşık %17,5'idir. İndirim uygulanmazsa %22,5 civarıdır. Yani 10.000 TL brüt maaşlı bir çalışanın işverene toplam maliyeti yaklaşık 11.750 TL'dir."
                  />
                  <SSSSoru
                    soru="5 puanlık SGK indirimi kimler için geçerlidir?"
                    cevap="5510 sayılı Kanun'un 81/ı maddesi gereği, SGK prim borcu olmayan, kayıt dışı işçi çalıştırmayan ve diğer teşviklerden yararlanmayan tüm özel sektör işverenleri bu indirimden faydalanabilir."
                  />
                  <SSSSoru
                    soru="İş kazası prim oranı neden değişir?"
                    cevap="Kısa vadeli sigorta kolları (iş kazası ve meslek hastalığı) prim oranı, işyerinin tehlike sınıfına göre %1 ile %6,5 arasında değişir. Bu oran SGK tarafından işyerinin faaliyet koduna göre belirlenir."
                  />
                  <SSSSoru
                    soru="Kıdem tazminatı bu hesaplamaya dahil mi?"
                    cevap="Hayır. Bu araç sadece SGK işveren primleri ve işsizlik sigortası işveren payını hesaplar. Kıdem tazminatı karşılığı, yemek, yol, prim, ikramiye gibi ek kalemler dahil değildir."
                  />
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Related Links */}
        <section className="max-w-4xl mx-auto px-4 py-4">
          <div className="bg-white rounded-xl border border-gray-200 p-5">
            <h3 className="font-bold text-gray-900 mb-2">
              Sonuc Yorumu ve Aksiyon Onerisi
            </h3>
            <p className="text-sm text-gray-600 mb-3">
              Isveren maliyetini ucret artisi ve yan hak kararlarindan ayri
              dusunmeyin. Aylik ve yillik maliyet etkisini teklif sureciyle
              birlikte planlamak daha saglikli bir butce yonetimi sunar.
            </p>
            <div className="flex flex-wrap gap-2 text-sm">
              <Link
                href="/araclar/maas-zammi-hesaplayici"
                className="text-indigo-600 hover:underline"
              >
                Maas zammi senaryosu
              </Link>
              <Link
                href="/yazilim/ucret-ve-yan-hak-yonetimi"
                className="text-indigo-600 hover:underline"
              >
                Ucret ve yan hak yonetimi
              </Link>
              <Link
                href="/cozumler/avans-finans"
                className="text-indigo-600 hover:underline"
              >
                Avans ve finans modulu
              </Link>
            </div>
          </div>
        </section>

        <section className="max-w-4xl mx-auto px-4 py-4">
          <div className="bg-white rounded-xl border border-gray-200 p-5">
            <h2 className="text-xl font-bold text-gray-900 mb-2">
              Istihdam Butcesi Kontrol Cizelgesi
            </h2>
            <h3 className="text-base font-semibold text-gray-800 mb-2">
              Prim, tesvik ve yan hak etkisi birlikte planlanir
            </h3>
            <p className="text-sm text-gray-600">
              Isveren maliyetini aylik ve yillik bazda takip ederek ucret,
              tesvik ve yan hak kombinasyonunu daha dengeli optimize
              edebilirsiniz.
            </p>
          </div>
        </section>

        <section className="max-w-4xl mx-auto px-4 py-8">
          <RelatedLinks
            pageType="tool"
            currentSlug="isveren-maliyeti-hesaplayici"
            title="İlgili Sayfalar"
            maxLinks={4}
          />
        </section>

        {/* CTA */}
        <section className="max-w-4xl mx-auto px-4 py-12">
          <div className="bg-indigo-600 rounded-2xl p-8 text-center text-white">
            <h2 className="text-2xl font-bold mb-4">
              İstihdam Maliyetlerini Optimize Edin
            </h2>
            <p className="text-indigo-100 mb-6 max-w-xl mx-auto">
              IKAI ile bordro süreçlerinizi otomatikleştirin. Teşvik takibi,
              maliyet analizi ve yasal uyum tek platformda.
            </p>
            <Link
              href="/register"
              className="inline-flex items-center gap-2 bg-white text-indigo-700 px-6 py-3 rounded-xl font-semibold hover:bg-indigo-50 transition-colors"
            >
              Ücretsiz Deneyin
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </section>
      </div>
    </>
  );
}
